import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useParams,
  useNavigate,
  useLocation
} from "react-router-dom";
import { AuthProvider } from "./AuthContext";
import HomePage from "./components/HomePage";
import CustomerLogin from "./components/customer/CustomerLogin";
import CustomerHome from "./components/customer/CustomerHome";
import SkipBinWizard from "./components/skipbins/Wizard";
import ProtectedRoute from "./ProtectedRoute";
import QuotationView from "./components/customer/QuotationView";
import CoolroomWizard from "./components/coolrooms/CoolroomWizard";
import RequestWizard from "./components/wizard/RequestWizard";
import QuotationRequestHome from "./components/customer/QuotationRequestHome";
import KruozAPI from "./KruozAPI";
import { useAuth } from "./AuthContext";
import QuotationOrderHome from "./components/customer/QuotationOrderHome";
import ChatAndQuoate from "./components/conversion/ChatAndQuoate";
import WizardBuilder from "./components/conversion/WizardBuilder";

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/customer-login" element={<CustomerLogin />} />
          <Route path="/skip-bin-wizard" element={<SkipBinWizard />} />
          <Route path="/coolroom-wizard" element={<CoolroomWizard />} />
          <Route path="/quotation/:id" element={<QuotationView />} />
          <Route path="/:companyCode" element={<RequestWizardWrapper />} />
          <Route path="/:companyCode/:requestId" element={<RequestWizardWrapper />} /> 
          <Route path="/chat-quote" element={<ChatAndQuoate />} />
          <Route path="/wizard-builder/:companyCode" element={<WizardBuilder />} />
          
          {/* Protected routes */}
          
          <Route
            path="/request/:requestId"
            element={<QuotationRequestHome />}
          />
          <Route
            path="/order/:orderNumber"
            element={<QuotationOrderHome />}
          />
          <Route
            path="/customer-home"
            element={
              <ProtectedRoute>
                <CustomerHome />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

const RequestWizardWrapper = () => {
  const { companyCode, requestId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [company, setCompany] = useState(null);
  const [properties, setProperties] = useState(null);
  const [existingRequest, setExistingRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, login } = useAuth();

  // Capture uid from admin portal (e.g., /MCH?uid=GUID) and persist for the browser session
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const uid = params.get('uid');
    if (uid) {
      sessionStorage.setItem('adminPortalUid', uid);
    }
  }, [location.search]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch company data
        const companyResponse = await KruozAPI.get(`Company/QuotationCompany?code=${companyCode}`);
        const companyData = {
          ...companyResponse.data,
          quotationTemplate: JSON.parse(companyResponse.data.quotationTemplate)
        };
        
        // Fetch properties
        const propertiesResponse = await KruozAPI.get('assets/property?companyId=' + companyData.id);
        const propertiesData = propertiesResponse.data;

        // If requestId exists, fetch the existing request
        if (requestId) {
          try {
            const requestResponse = await KruozAPI.get(`Quotation/QuotationRequest/${requestId}`);
            const requestData = requestResponse.data;
            
            // Parse the content field if it exists
            if (requestData.content) {
              requestData.content = JSON.parse(requestData.content);
              
              // Check if the step is 'preview' and navigate if it is
              debugger;
              if (requestData.step === 'preview') {
                navigate(`/request/${requestId}`);
                return;
              }
              
              // Update the company's quotation template with the saved data
              if (requestData.content.wizardSteps) {
                companyData.quotationTemplate.wizardSteps = requestData.content.wizardSteps;
              }
            }
            
            setExistingRequest(requestData);
          } catch (err) {
            console.error('Error fetching request data:', err);
            throw new Error('Failed to load existing request data');
          }
        }

        setCompany(companyData);
        setProperties(propertiesData);
        setError(null);
      } catch (err) {
        console.error('Error in fetchData:', err);
        if (err.response) {
          setError(err.response.data || 'An error occurred while fetching the data.');
        } else if (err.request) {
          setError('No response received from the server. Please try again later.');
        } else {
          setError('An unexpected error occurred. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [companyCode, requestId, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="p-8 bg-white rounded-lg shadow-md max-w-md w-full">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            <h2 className="mt-4 text-xl font-semibold text-gray-700">
              Loading data...
            </h2>
            <p className="mt-2 text-gray-500">
              Please wait while we prepare everything.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="p-8 bg-white rounded-lg shadow-md max-w-md w-full">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-700">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!company || !properties) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="p-8 bg-white rounded-lg shadow-md max-w-md w-full">
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            Data Not Found
          </h2>
          <p className="text-gray-700">
            We couldn't find the necessary information. Please check the URL and
            try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <RequestWizard
      company={company}
      properties={properties}
      user={user}
      login={login}
      existingRequest={existingRequest}
    />
  );
};

export default App;
